import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Repeat } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * WeekView — Q32 Phase B
 *
 * Google Calendar style timeline. 7 day columns, hour rows from 6 AM to
 * 11 PM. Events are absolutely positioned blocks based on start_time and
 * end_time (defaults to a 90-minute block when end_time is missing).
 *
 * Props:
 *   itemsByDate — { "YYYY-MM-DD": [agendaRow, ...] }
 */
export default function WeekView({ itemsByDate }) {
  const [anchor, setAnchor] = useState(() => startOfWeek(new Date()));
  const navigate = useNavigate();

  const days = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(anchor);
      d.setDate(anchor.getDate() + i);
      return { iso: isoOf(d), date: d };
    });
  }, [anchor]);

  const todayIso = new Date().toISOString().slice(0, 10);
  const startHour = 6;
  const endHour = 23;
  const hours = endHour - startHour + 1; // inclusive
  const rowHeight = 44; // px per hour
  const totalHeight = hours * rowHeight;

  const shift = (n) => {
    const d = new Date(anchor);
    d.setDate(d.getDate() + n * 7);
    setAnchor(d);
  };

  const weekLabel = `${days[0].date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${days[6].date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;

  return (
    <div data-testid="calendar-week-view" className="space-y-3">
      <div className="flex items-center justify-between">
        <button
          data-testid="week-prev"
          onClick={() => shift(-1)}
          aria-label="Previous week"
          className="w-8 h-8 rounded-full bg-white border border-black/[0.06] flex items-center justify-center active:bg-[#F1F0EA]"
        >
          <ChevronLeft size={16} />
        </button>
        <h2 data-testid="week-label" className="text-sm font-extrabold text-[#111] tracking-tight">
          {weekLabel}
        </h2>
        <button
          data-testid="week-next"
          onClick={() => shift(1)}
          aria-label="Next week"
          className="w-8 h-8 rounded-full bg-white border border-black/[0.06] flex items-center justify-center active:bg-[#F1F0EA]"
        >
          <ChevronRight size={16} />
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-black/[0.06] overflow-hidden">
        <div className="grid" style={{ gridTemplateColumns: '36px repeat(7, 1fr)' }}>
          <div className="bg-[#FAFAF7] border-b border-black/[0.04]" />
          {days.map(d => (
            <div
              key={d.iso}
              data-testid={`week-day-${d.iso}`}
              className={`text-center py-2 border-b border-l border-black/[0.04] ${d.iso === todayIso ? 'bg-[#B6E600]/10' : 'bg-[#FAFAF7]'}`}
            >
              <p className="text-[9px] font-bold uppercase text-[#9B9EA3]">
                {d.date.toLocaleDateString(undefined, { weekday: 'short' })}
              </p>
              <p className={`text-sm ${d.iso === todayIso ? 'font-extrabold text-[#3F5200]' : 'font-bold text-[#111]'}`}>
                {d.date.getDate()}
              </p>
            </div>
          ))}
        </div>

        <div className="relative overflow-auto" style={{ maxHeight: '60vh' }}>
          <div className="grid" style={{ gridTemplateColumns: '36px repeat(7, 1fr)', height: totalHeight }}>
            {/* Hour column */}
            <div className="relative bg-[#FAFAF7]">
              {Array.from({ length: hours }, (_, i) => (
                <div
                  key={i}
                  className="absolute left-0 right-0 text-[9px] font-semibold text-[#9B9EA3] text-center"
                  style={{ top: i * rowHeight - 6 }}
                >
                  {fmtHour(startHour + i)}
                </div>
              ))}
            </div>

            {/* Day columns */}
            {days.map(d => {
              const events = itemsByDate[d.iso] || [];
              return (
                <div
                  key={d.iso}
                  className="relative border-l border-black/[0.04]"
                  data-testid={`week-col-${d.iso}`}
                >
                  {/* Hour lines */}
                  {Array.from({ length: hours }, (_, i) => (
                    <div
                      key={i}
                      className="absolute left-0 right-0 border-t border-black/[0.04]"
                      style={{ top: i * rowHeight }}
                    />
                  ))}
                  {events.map((row, i) => {
                    const block = blockGeometry(row, startHour, rowHeight);
                    if (!block) return null;
                    return (
                      <button
                        key={`${row.plan.id}-${i}`}
                        data-testid={`week-event-${row.plan.id}`}
                        onClick={() => navigate(`/plan/${row.plan.id}`)}
                        style={{ top: block.top, height: block.height }}
                        className="absolute left-1 right-1 bg-[#B6E600] text-[#111] rounded-lg px-1.5 py-1 text-left overflow-hidden border border-[#3F5200]/20 active:opacity-80 shadow-sm"
                      >
                        <div className="flex items-center gap-0.5">
                          <p className="text-[10px] font-extrabold truncate flex-1 leading-tight">
                            {row.plan.title}
                          </p>
                          {row.is_recurring && <Repeat size={8} strokeWidth={3} className="flex-shrink-0" />}
                        </div>
                        <p className="text-[8px] font-semibold text-[#3F5200] leading-tight">
                          {(row.plan.start_time || '').slice(0, 5)}
                        </p>
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function blockGeometry(row, startHour, rowHeight) {
  const startMin = parseHHMM(row.plan?.start_time);
  if (startMin === null) return null;
  let endMin = parseHHMM(row.plan?.end_time);
  if (endMin === null || endMin <= startMin) endMin = startMin + 90;

  const startOffset = (startMin / 60 - startHour) * rowHeight;
  const height = Math.max(((endMin - startMin) / 60) * rowHeight, 18);
  if (startOffset + height < 0) return null;
  return {
    top: Math.max(0, startOffset),
    height,
  };
}

function parseHHMM(hhmm) {
  if (!hhmm) return null;
  const parts = String(hhmm).split(':');
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1] || '0', 10);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}

function fmtHour(h) {
  const period = h >= 12 ? 'p' : 'a';
  const display = ((h + 11) % 12) + 1;
  return `${display}${period}`;
}

function startOfWeek(d) {
  const day = (d.getDay() + 6) % 7; // Monday=0
  const start = new Date(d);
  start.setDate(d.getDate() - day);
  start.setHours(0, 0, 0, 0);
  return start;
}

function isoOf(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}
